<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/onboarding';
    import { computed } from 'vue';
    import { handleQueryRetry } from '@/utils/helpers';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateOnboardingStep from '@/components/onboarding/CreateOnboardingStep.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import PageCard from '@/components/PageCard.vue';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem } from '@/types';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.administration',
        },
        {
            title: 'menu.onboarding',
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.ONBOARDING, selectedCompanyId],
        queryFn: getOnboarding,
        retry: handleQueryRetry,
    });

    async function getOnboarding() {
        const { data } = await axios.get(
            route('v2.onboarding.table', { company: selectedCompanyId.value }),
        );
        return data.data;
    }
</script>

<template>
    <Head :title="$t('onboarding.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.ONBOARDING"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :default-column-visibility="{
                    order: false,
                }"
                :default-sorting="[
                    {
                        id: 'order',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('onboarding.page_header') }}
                    </CardTitle>
                </template>
                <template #actions="{ table }">
                    <CreateOnboardingStep :table="table" />
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
